import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { unionBy } from 'lodash';
import { ChatMessageData, ChatMessagesStore } from './types';
import { addChatNewMessage, fetchChatMessages, setMessageViewed } from './thunk';
import { ChatRoomsData } from '../chatRooms/types';
import { ViewedMessage } from '../../pages/types';

export const MESSAGES_PER_PAGE = 350;

const initialState: ChatMessagesStore = {
  entities: {},
  loading: 'idle',
};

export const chatMessages = createSlice({
  name: 'chatMessages',
  initialState,
  reducers: {
    resetOffset: (state, action: PayloadAction<string>) => {
      if (state.entities[action.payload]) {
        state.entities[action.payload].offset = 0;
        state.entities[action.payload].isInfiniteDisabled = false;
      }
    },
    addMessage: (state, action: PayloadAction<ChatMessageData | null>) => {
      if (!action.payload) return;

      if (!state.entities[action.payload.room_id]?.messages) {
        state.entities[action.payload.room_id] = {
          messages: [],
          offset: 0,
          isInfiniteDisabled: false,
        };
      }

      const message = state.entities[action.payload.room_id].messages.find((item) => item.id === action.payload?.id);
      if (message) {
        message.status = action.payload.status;
        message.updated_at = action.payload.updated_at;
      } else {
        state.entities[action.payload.room_id].messages.push({
          id: action.payload.id,
          room_id: action.payload.room_id,
          sender_id: action.payload.sender_id,
          receiver_id: action.payload.receiver_id,
          message: action.payload.message,
          updated_at: action.payload.updated_at,
          status: action.payload.status,
          messageType: action.payload?.messageType ?? 'text',
          fileInfo: action.payload?.fileInfo,
        });
      }
    },
    setAgoraMessageViewed: (state, action: PayloadAction<ViewedMessage>) => {
      const message = state.entities[action.payload.room_id].messages.find(
        (item) => item.id === action.payload.message_id
      );
      if (message) {
        message.status = 'viewed';
      }
    },
    resetUnreadMessages: (state, action: PayloadAction<string>) => {
      Object.keys(state.entities).forEach((key) => {
        state.entities[key].messages.forEach((m) => {
          if (m.receiver_id === action.payload) {
            m.status = 'viewed';
          }
        });
      });
    },
    resetChatMessages: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchChatMessages.pending, (state) => {
      state.loading = 'pending';
    });
    builder.addCase(fetchChatMessages.fulfilled, (state, action: PayloadAction<ChatMessageData[]>) => {
      if (action.payload.length) {
        if (!state.entities[action.payload[0].room_id]?.messages) {
          state.entities[action.payload[0].room_id] = {
            messages: [],
            offset: MESSAGES_PER_PAGE,
            isInfiniteDisabled: false,
          };
        } else {
          state.entities[action.payload[0].room_id].offset += MESSAGES_PER_PAGE;
        }
        state.entities[action.payload[0].room_id].messages = unionBy(
          state.entities[action.payload[0].room_id].messages,
          action.payload,
          'id'
        );
      } else {
        // @ts-ignore
        if (action.payload?.roomId) {
          // @ts-ignore
          if (state.entities[action.payload?.roomId]) {
            // @ts-ignore
            state.entities[action.payload?.roomId].isInfiniteDisabled = true;
          }
        }
      }
      state.loading = 'succeeded';
    });
    builder.addCase(addChatNewMessage.fulfilled, (state, action: PayloadAction<ChatRoomsData>) => {
      if (!state.entities[action.payload.room.id]) {
        state.entities[action.payload.room.id] = {
          messages: [],
          offset: 0,
          isInfiniteDisabled: false,
        };
      }
      if (state.entities[action.payload.room.id]?.messages) {
        const message = state.entities[action.payload.room.id].messages.find(
          (item) => item.id === action.payload.last_message.id
        );
        if (message) {
          message.status = action.payload.last_message.status;
          if (action.payload.last_message.messageType !== 'video') {
            message.message = action.payload.last_message.message;
          }
          message.messageType = action.payload.last_message?.messageType ?? 'text';
          message.fileInfo = action.payload.last_message?.fileInfo;
        } else {
          state.entities[action.payload.room.id].messages.push({
            id: action.payload.last_message.id,
            room_id: action.payload.room.id,
            sender_id: action.payload.last_message.sender_id,
            receiver_id: action.payload.last_message.receiver_id,
            message: action.payload.last_message.message,
            updated_at: action.payload.last_message.updated_at,
            status: action.payload.last_message.status,
            messageType: action.payload.last_message?.messageType ?? 'text',
            fileInfo: action.payload.last_message?.fileInfo,
          });
        }
      }
    });
    builder.addCase(setMessageViewed.fulfilled, (state, action: PayloadAction<ChatMessageData>) => {
      const message = state.entities[action.payload.room_id].messages.find((item) => item.id === action.payload.id);
      if (message) {
        message.status = action.payload.status;
      }
    });
  },
});

export default chatMessages.reducer;
export const { addMessage, resetOffset, setAgoraMessageViewed, resetUnreadMessages, resetChatMessages } =
  chatMessages.actions;
